import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

interface StatusContextValue {
    announce: (message: string) => void;
}

const StatusContext = createContext<StatusContextValue | null>(null);

/**
 * Single shared `aria-live="polite"` region — Mail surfaces wrap the SPA in
 * this provider so feature code can call `announce("Saved")` /
 * `announce("Sent")` without each component shipping its own live region.
 * The visual UI shows nothing; screen readers read the message.
 */
export function StatusAnnouncerProvider({ children }: { children: ReactNode }) {
    const [message, setMessage] = useState("");
    const clearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const announce = useCallback((value: string) => {
        if (clearTimer.current) clearTimeout(clearTimer.current);
        // Set then clear so the same message twice in a row still triggers
        // a screen-reader announcement (live regions only re-announce on a
        // text change).
        setMessage("");
        requestAnimationFrame(() => setMessage(value));
        clearTimer.current = setTimeout(() => setMessage(""), 4000);
    }, []);

    useEffect(() => () => {
        if (clearTimer.current) clearTimeout(clearTimer.current);
    }, []);

    return (
        <StatusContext.Provider value={{ announce }}>
            {children}
            <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
                {message}
            </div>
        </StatusContext.Provider>
    );
}

export function useStatusAnnouncer(): StatusContextValue {
    const ctx = useContext(StatusContext);
    if (!ctx) {
        throw new Error("useStatusAnnouncer must be used inside <StatusAnnouncerProvider>.");
    }
    return ctx;
}
